import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Loader2, Mail, X } from 'lucide-react'

import { Event, IdentityRole } from '../model/Event'
import { useApplicationStore } from '../stores/useApplicationStore'
import EventEndpoints from '../endpoints/EventEndpoints'
import ReportEndpoints from '../endpoints/ReportEndpoints'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'

interface EmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const EmailDialog: React.FC<EmailDialogProps> = ({ open, onOpenChange }) => {
  const { events } = useApplicationStore()

  const [eventId, setEventId] = useState('')
  const [roles, setRoles] = useState<IdentityRole[]>([])
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setEventId('')
      setRoles([])
      setSelectedRoleIds([])
      setSubject('')
      setContent('')
    }
  }, [open])

  const handleEventChange = async (id: string) => {
    setEventId(id)
    setLoading(true)
    try {
      const event = await EventEndpoints.getEvent(parseInt(id))
      setRoles(event.roles || [])
      setSelectedRoleIds(event.roles.map((r) => r.id))
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to load event roles'))
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required')
      return
    }
    if (selectedRoleIds.length === 0) {
      toast.error('Please select at least one role')
      return
    }

    setSending(true)
    try {
      await ReportEndpoints.sendAdminEmail({
        roles: selectedRoleIds,
        subject: subject.trim(),
        content: content.trim(),
      })
      toast.success('Email sent successfully')
      onOpenChange(false)
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to send email'))
    } finally {
      setSending(false)
    }
  }

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const selectedRoles = roles.filter((r) => selectedRoleIds.includes(r.id))
  const unselectedRoles = roles.filter((r) => !selectedRoleIds.includes(r.id))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Email
          </DialogTitle>
          <DialogDescription>
            Send an email to identities provisioned with specific roles in an event.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Event selector */}
          <div className="space-y-2">
            <Label>Event</Label>
            <Select value={eventId} onValueChange={handleEventChange} disabled={loading || sending}>
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id.toString()}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Roles */}
          {eventId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Recipients by Role</Label>
                <div className="flex gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setSelectedRoleIds(roles.map((r) => r.id))}
                    disabled={loading || sending || selectedRoleIds.length === roles.length}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setSelectedRoleIds([])}
                    disabled={loading || sending || selectedRoleIds.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg bg-muted/20 min-h-[44px]">
                  {roles.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No roles in this event</p>
                  ) : (
                    roles
                      .sort((a, b) => a.internalName.localeCompare(b.internalName))
                      .map((role) => {
                        const isSelected = selectedRoleIds.includes(role.id)
                        return (
                          <Badge
                            key={role.id}
                            variant={isSelected ? 'default' : 'outline'}
                            className="cursor-pointer select-none"
                            onClick={() => !sending && toggleRole(role.id)}
                          >
                            {role.internalName}
                            {isSelected && <X className="h-3 w-3 ml-1" />}
                          </Badge>
                        )
                      })
                  )}
                </div>
              )}
              {selectedRoleIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedRoleIds.length} of {roles.length} role{roles.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject *</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              placeholder="Email subject line"
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="email-content">Message</Label>
            <Textarea
              id="email-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={sending}
              rows={6}
              placeholder="Email body content..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!subject.trim() || selectedRoleIds.length === 0 || !eventId || sending}
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
